import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useCompany } from '../../hooks/useCompany';
import { 
  sessionAPI, tellerTransactionAPI, smsParseAPI, tellerAccountAPI,
  allocationAPI, stockSalesAPI, cashRequestAPI, tellerSessionAPI, stockAPI
} from '../../lib/api';
import { toast } from 'sonner';
import {
  DollarSign, Banknote, ArrowUpRight, ArrowDownRight, Clock,
  Plus, Search, Filter, X, RefreshCw, Power, PowerOff,
  MessageSquare, Upload, ShoppingBag, Send, CheckCircle,
  AlertCircle, Smartphone, Receipt, CreditCard, Repeat,
  Building2, Users, Truck, GraduationCap, Wallet, Landmark
} from 'lucide-react';

// Transaction type definitions per Teller Document (Double-Entry)
const TRANSACTION_TYPES = [
  // Cash & Liquidity
  { value: 'cash_from_vault', label: 'Cash Received from Vault', icon: ArrowDownRight, color: 'text-green-600', direction: 'in', group: 'Cash' },
  { value: 'cash_to_vault', label: 'Cash Sent to Vault', icon: ArrowUpRight, color: 'text-red-600', direction: 'out', group: 'Cash' },
  { value: 'cash_from_bank', label: 'Cash Received from Bank', icon: ArrowDownRight, color: 'text-green-600', direction: 'in', group: 'Cash' },
  { value: 'customer_deposit', label: 'Customer Deposit', icon: ArrowDownRight, color: 'text-green-600', direction: 'in', group: 'Customer' },
  { value: 'pos_cashout', label: 'POS Cash-Out (Withdrawal)', icon: CreditCard, color: 'text-red-600', direction: 'out', group: 'Customer' },
  // Mobile Money
  { value: 'mtn_cash_in', label: 'MTN Cash-In', icon: Smartphone, color: 'text-green-600', direction: 'in', group: 'Mobile Money' },
  { value: 'mtn_cash_out', label: 'MTN Cash-Out', icon: Smartphone, color: 'text-red-600', direction: 'out', group: 'Mobile Money' },
  { value: 'orange_cash_in', label: 'Orange Cash-In', icon: Smartphone, color: 'text-green-600', direction: 'in', group: 'Mobile Money' },
  { value: 'orange_cash_out', label: 'Orange Cash-Out', icon: Smartphone, color: 'text-red-600', direction: 'out', group: 'Mobile Money' },
  { value: 'bnb_cash_in', label: 'BnB Cash-In', icon: Smartphone, color: 'text-green-600', direction: 'in', group: 'Mobile Money' },
  { value: 'bnb_cash_out', label: 'BnB Cash-Out', icon: Smartphone, color: 'text-red-600', direction: 'out', group: 'Mobile Money' },
  { value: 'xpress_cash_in', label: 'Xpress Cash-In', icon: Smartphone, color: 'text-green-600', direction: 'in', group: 'Mobile Money' },
  { value: 'xpress_cash_out', label: 'Xpress Cash-Out', icon: Smartphone, color: 'text-red-600', direction: 'out', group: 'Mobile Money' },
  // Float
  { value: 'float_purchase', label: 'Float Purchase (Buy Float)', icon: Wallet, color: 'text-red-600', direction: 'out', group: 'Float', needsProvider: true },
  { value: 'float_liquidation', label: 'Float Liquidation (Sell Float)', icon: Wallet, color: 'text-green-600', direction: 'in', group: 'Float', needsProvider: true },
  // Cards
  { value: 'card_purchase', label: 'Card Purchase/Sale (MTN/Orange)', icon: CreditCard, color: 'text-green-600', direction: 'in', group: 'Cards' },
  // Orange POS Agent
  { value: 'orange_pos_cash_out', label: 'Cash to Orange POS Agent', icon: Send, color: 'text-red-600', direction: 'out', group: 'Agent' },
  { value: 'orange_pos_cash_in', label: 'Cash from Orange POS Agent', icon: ArrowDownRight, color: 'text-green-600', direction: 'in', group: 'Agent' },
  // Remittance
  { value: 'remittance_send', label: 'Remittance Send (MoneyGram/WU)', icon: Send, color: 'text-green-600', direction: 'in', group: 'Remittance' },
  { value: 'remittance_payout', label: 'Remittance Payout', icon: Truck, color: 'text-red-600', direction: 'out', group: 'Remittance' },
  // FX
  { value: 'fx_buy', label: 'FX Buy (Customer buys foreign)', icon: Repeat, color: 'text-blue-600', direction: 'fx', group: 'FX' },
  { value: 'fx_sell', label: 'FX Sell (Customer sells foreign)', icon: Repeat, color: 'text-purple-600', direction: 'fx', group: 'FX' },
  // Loans
  { value: 'loan_disbursement', label: 'Loan Disbursement', icon: Banknote, color: 'text-red-600', direction: 'out', group: 'Loans' },
  { value: 'loan_repayment', label: 'Loan Repayment', icon: Banknote, color: 'text-green-600', direction: 'in', group: 'Loans' },
  // Expenses & Salary
  { value: 'branch_expense', label: 'Branch / Petty Cash Expense', icon: Receipt, color: 'text-red-600', direction: 'out', group: 'Expenses' },
  { value: 'salary_advance_disburse', label: 'Salary Advance Disbursement', icon: Users, color: 'text-red-600', direction: 'out', group: 'Expenses' },
  { value: 'salary_advance_recovery', label: 'Salary Advance Recovery', icon: Users, color: 'text-green-600', direction: 'in', group: 'Expenses' },
  // Stock
  { value: 'stock_sale', label: 'Stock Sale', icon: ShoppingBag, color: 'text-green-600', direction: 'in', group: 'Stock' },
];

// USD denominations
const USD_DENOMS = [100, 50, 20, 10, 5, 2, 1];
// LRD denominations
const LRD_DENOMS = [5000, 1000, 500, 100, 50, 20, 10, 5];

const formatCurrency = (amount, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }
  return `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

// KPI Strip Component
function KPIStrip({ session, teller }) {
  const usdBalance = teller?.usd_current_balance || 0;
  const lrdBalance = teller?.lrd_current_balance || 0;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div className="p-4 rounded-lg border border-green-200 bg-green-50">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="h-4 w-4 text-green-600" />
          <span className="text-sm text-green-600 font-medium">USD Balance</span>
        </div>
        <p className="text-2xl font-bold text-green-700">{formatCurrency(usdBalance, 'USD')}</p>
      </div>
      <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
        <div className="flex items-center gap-2 mb-1">
          <Banknote className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-blue-600 font-medium">LRD Balance</span>
        </div>
        <p className="text-2xl font-bold text-blue-700">{formatCurrency(lrdBalance, 'LRD')}</p>
      </div>
      <div className="p-4 rounded-lg border border-purple-200 bg-purple-50">
        <div className="flex items-center gap-2 mb-1">
          <Receipt className="h-4 w-4 text-purple-600" />
          <span className="text-sm text-purple-600 font-medium">Today&apos;s Transactions</span>
        </div>
        <p className="text-2xl font-bold text-purple-700">{session?.total_transactions || 0}</p>
      </div>
      <div className="p-4 rounded-lg border border-orange-200 bg-orange-50">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="h-4 w-4 text-orange-600" />
          <span className="text-sm text-orange-600 font-medium">Session Status</span>
        </div>
        <p className={`text-lg font-bold ${session?.status === 'open' ? 'text-green-600' : 'text-red-600'}`}>
          {session?.status === 'open' ? 'ACTIVE' : 'NOT ACTIVE'}
        </p>
      </div>
    </div>
  );
}

// Denomination Counter Component
function DenominationCounter({ currency, denominations, onChange }) {
  const denoms = currency === 'USD' ? USD_DENOMS : LRD_DENOMS;
  
  const calculateTotal = () => {
    return denoms.reduce((sum, d) => sum + (denominations[d] || 0) * d, 0);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-medium">{currency} Denominations</span>
        <span className="text-lg font-bold">{formatCurrency(calculateTotal(), currency)}</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {denoms.map(d => (
          <div key={d} className="flex flex-col items-center p-2 border rounded-lg bg-muted/30">
            <span className="text-xs text-muted-foreground">{currency === 'USD' ? '$' : 'L$'}{d}</span>
            <input
              type="number"
              min="0"
              value={denominations[d] || 0}
              onChange={(e) => onChange({ ...denominations, [d]: parseInt(e.target.value) || 0 })}
              className="w-16 text-center px-2 py-1 border rounded mt-1 text-sm"
            />
            <span className="text-xs text-muted-foreground mt-1">
              = {formatCurrency((denominations[d] || 0) * d, currency)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Open Session Modal - Confirms Cash and Stock Allocations
function OpenSessionModal({ isOpen, onClose, tellerId, allocations, companyId }) {
  const queryClient = useQueryClient();
  const [usdDenoms, setUsdDenoms] = useState({});
  const [lrdDenoms, setLrdDenoms] = useState({});
  const [stockConfirmations, setStockConfirmations] = useState({});
  const [activeTab, setActiveTab] = useState('cash');

  // Fetch stock allocations for this teller
  const { data: stockAllocations = [] } = useQuery({
    queryKey: ['stock-allocations-pending', tellerId],
    queryFn: () => stockAPI.getAllocations(companyId, 'pending').then(r => 
      r.data.filter(a => a.teller_account_id === tellerId)
    ),
    enabled: isOpen && !!tellerId && !!companyId,
  });

  const expectedUsd = allocations?.filter(a => a.currency === 'USD').reduce((s, a) => s + a.amount, 0) || 0;
  const expectedLrd = allocations?.filter(a => a.currency === 'LRD').reduce((s, a) => s + a.amount, 0) || 0;
  
  const confirmedUsd = USD_DENOMS.reduce((sum, d) => sum + (usdDenoms[d] || 0) * d, 0);
  const confirmedLrd = LRD_DENOMS.reduce((sum, d) => sum + (lrdDenoms[d] || 0) * d, 0);

  // Initialize stock confirmations when allocations load
  useEffect(() => {
    if (stockAllocations.length > 0) {
      const initial = {};
      stockAllocations.forEach(alloc => {
        initial[alloc.id] = { confirmed: false, quantity: alloc.quantity };
      });
      setStockConfirmations(initial);
    }
  }, [stockAllocations]);

  const hasStockAllocations = stockAllocations.length > 0;
  const hasCashAllocations = expectedUsd > 0 || expectedLrd > 0;
  const allStockConfirmed = stockAllocations.every(a => stockConfirmations[a.id]?.confirmed);

  const openMutation = useMutation({
    mutationFn: (data) => tellerSessionAPI.openWithConfirmation(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['teller-session']);
      queryClient.invalidateQueries(['teller-account']);
      queryClient.invalidateQueries(['teller-stock']);
      queryClient.invalidateQueries(['stock-allocations-pending']);
      toast.success('Session opened successfully!');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to open session'),
  });

  const handleSubmit = () => {
    // If no denominations entered but allocations exist, use expected amounts
    const finalUsd = confirmedUsd > 0 ? confirmedUsd : expectedUsd;
    const finalLrd = confirmedLrd > 0 ? confirmedLrd : expectedLrd;
    
    openMutation.mutate({
      teller_account_id: tellerId,
      confirmed_usd: finalUsd,
      confirmed_lrd: finalLrd,
      confirmed_denominations: { USD: usdDenoms, LRD: lrdDenoms },
      stock_confirmations: Object.entries(stockConfirmations)
        .filter(([_, v]) => v.confirmed)
        .map(([id, v]) => ({ allocation_id: id, quantity: v.quantity })),
    });
  };

  const handleQuickConfirm = () => {
    // Auto-fill denominations to match expected amount using largest bills first
    if (expectedUsd > 0) {
      const usd = {};
      let remaining = expectedUsd;
      for (const d of USD_DENOMS) {
        const count = Math.floor(remaining / d);
        if (count > 0) { usd[d] = count; remaining -= count * d; }
      }
      setUsdDenoms(usd);
    }
    if (expectedLrd > 0) {
      const lrd = {};
      let remaining = expectedLrd;
      for (const d of LRD_DENOMS) {
        const count = Math.floor(remaining / d);
        if (count > 0) { lrd[d] = count; remaining -= count * d; }
      }
      setLrdDenoms(lrd);
    }
    toast.success('Denominations auto-filled to match expected allocation');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Open Session - Confirm Allocations</h2>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
          </div>

          {/* Tab Selector (if both cash and stock) */}
          {hasStockAllocations && hasCashAllocations && (
            <div className="flex gap-2 mb-6 border-b border-border pb-2">
              <button
                onClick={() => setActiveTab('cash')}
                className={`px-4 py-2 rounded-t-lg font-medium text-sm ${activeTab === 'cash' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                Cash ({formatCurrency(expectedUsd)} + {formatCurrency(expectedLrd, 'LRD')})
              </button>
              <button
                onClick={() => setActiveTab('stock')}
                className={`px-4 py-2 rounded-t-lg font-medium text-sm ${activeTab === 'stock' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
              >
                Stock ({stockAllocations.length} items)
              </button>
            </div>
          )}

          {/* Cash Confirmation Tab */}
          {(activeTab === 'cash' || !hasStockAllocations) && hasCashAllocations && (
            <>
              {/* Expected vs Confirmed */}
              <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Expected from Vault</p>
                  <p className="font-semibold">{formatCurrency(expectedUsd, 'USD')} / {formatCurrency(expectedLrd, 'LRD')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Your Count</p>
                  <p className={`font-semibold ${confirmedUsd !== expectedUsd || confirmedLrd !== expectedLrd ? 'text-orange-600' : 'text-green-600'}`}>
                    {formatCurrency(confirmedUsd, 'USD')} / {formatCurrency(confirmedLrd, 'LRD')}
                  </p>
                </div>
              </div>

              {/* Quick Confirm Button */}
              <button
                type="button"
                onClick={handleQuickConfirm}
                className="w-full mb-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                data-testid="quick-confirm-btn"
              >
                Quick Confirm — Accept Expected Amount ({formatCurrency(expectedUsd, 'USD')}{expectedLrd > 0 ? ` + ${formatCurrency(expectedLrd, 'LRD')}` : ''})
              </button>

              {/* USD Denominations */}
              <div className="mb-6">
                <DenominationCounter currency="USD" denominations={usdDenoms} onChange={setUsdDenoms} />
              </div>

              {/* LRD Denominations */}
              <div className="mb-6">
                <DenominationCounter currency="LRD" denominations={lrdDenoms} onChange={setLrdDenoms} />
              </div>

              {/* Variance Warning */}
              {(confirmedUsd !== expectedUsd || confirmedLrd !== expectedLrd) && (
                <div className="mb-4 p-3 bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  <span className="text-sm text-orange-700 dark:text-orange-300">
                    Variance detected: USD {formatCurrency(confirmedUsd - expectedUsd)}, LRD {formatCurrency(confirmedLrd - expectedLrd, 'LRD')}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Stock Confirmation Tab */}
          {(activeTab === 'stock' || !hasCashAllocations) && hasStockAllocations && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Confirm each stock item you received from the vault. Check the box once you verify the quantity.
              </p>
              {stockAllocations.map(alloc => (
                <div key={alloc.id} className="p-4 border border-border rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={stockConfirmations[alloc.id]?.confirmed || false}
                      onChange={(e) => setStockConfirmations(prev => ({
                        ...prev,
                        [alloc.id]: { ...prev[alloc.id], confirmed: e.target.checked }
                      }))}
                      className="h-5 w-5 rounded border-border"
                    />
                    <div>
                      <p className="font-medium">{alloc.item_name || alloc.stock_item_name}</p>
                      <p className="text-sm text-muted-foreground">SKU: {alloc.item_sku || alloc.stock_item_sku}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">{alloc.quantity} units</p>
                    <p className="text-sm text-muted-foreground">@ {formatCurrency(alloc.unit_price || 0)} each</p>
                  </div>
                </div>
              ))}
              
              {!allStockConfirmed && (
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    Please confirm all stock items before opening session
                  </span>
                </div>
              )}
            </div>
          )}

          {/* No allocations message */}
          {!hasCashAllocations && !hasStockAllocations && (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <p className="text-muted-foreground">No pending allocations to confirm.</p>
              <p className="text-sm text-muted-foreground mt-2">You can still open a session with zero balance.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-border">
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={openMutation.isPending || (hasStockAllocations && !allStockConfirmed)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {openMutation.isPending ? 'Opening...' : 'Confirm & Open Session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Close Session Modal
function CloseSessionModal({ isOpen, onClose, session, teller }) {
  const queryClient = useQueryClient();
  const [usdDenoms, setUsdDenoms] = useState({});
  const [lrdDenoms, setLrdDenoms] = useState({});

  const expectedUsd = teller?.usd_current_balance || 0;
  const expectedLrd = teller?.lrd_current_balance || 0;
  
  const countedUsd = USD_DENOMS.reduce((sum, d) => sum + (usdDenoms[d] || 0) * d, 0);
  const countedLrd = LRD_DENOMS.reduce((sum, d) => sum + (lrdDenoms[d] || 0) * d, 0);

  const closeMutation = useMutation({
    mutationFn: (data) => sessionAPI.close(session.id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['teller-session']);
      queryClient.invalidateQueries(['teller-account']);
      const variance = `USD: ${formatCurrency(res.data.variance_usd)}, LRD: ${formatCurrency(res.data.variance_lrd, 'LRD')}`;
      toast.success(`Session closed. Variance: ${variance}`);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to close session'),
  });

  const handleSubmit = () => {
    closeMutation.mutate({
      closing_usd: countedUsd,
      closing_lrd: countedLrd,
      closing_denominations: { USD: usdDenoms, LRD: lrdDenoms },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Close Session - Count Cash</h2>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-5 w-5" /></button>
          </div>

          {/* Expected vs Counted */}
          <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Expected Balance</p>
              <p className="font-semibold">{formatCurrency(expectedUsd, 'USD')} / {formatCurrency(expectedLrd, 'LRD')}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your Count</p>
              <p className={`font-semibold ${countedUsd !== expectedUsd || countedLrd !== expectedLrd ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(countedUsd, 'USD')} / {formatCurrency(countedLrd, 'LRD')}
              </p>
            </div>
          </div>

          <div className="mb-6"><DenominationCounter currency="USD" denominations={usdDenoms} onChange={setUsdDenoms} /></div>
          <div className="mb-6"><DenominationCounter currency="LRD" denominations={lrdDenoms} onChange={setLrdDenoms} /></div>

          {(countedUsd !== expectedUsd || countedLrd !== expectedLrd) && (
            <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="text-sm text-red-700">
                Variance: USD {formatCurrency(countedUsd - expectedUsd)}, LRD {formatCurrency(countedLrd - expectedLrd, 'LRD')}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted">Cancel</button>
            <button onClick={handleSubmit} disabled={closeMutation.isPending} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
              {closeMutation.isPending ? 'Closing...' : 'Close Session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// New Transaction Modal
function NewTransactionModal({ isOpen, onClose, session }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState('manual'); // 'manual' or 'ai'
  const [smsText, setSmsText] = useState('');
  const [formData, setFormData] = useState({
    transaction_type: 'cash_from_vault',
    currency: 'USD',
    amount: '',
    customer_name: '',
    customer_phone: '',
    reference: '',
    notes: '',
    fx_rate: '',
    fx_amount: '',
    provider: '',
    fee_amount: '',
  });

  const transactionMutation = useMutation({
    mutationFn: (data) => tellerTransactionAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['teller-transactions']);
      queryClient.invalidateQueries(['teller-session']);
      queryClient.invalidateQueries(['teller-account']);
      queryClient.invalidateQueries(['my-teller-account']);
      toast.success('Transaction posted successfully!');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to post transaction'),
  });

  const parseMutation = useMutation({
    mutationFn: (text) => smsParseAPI.parse(text),
    onSuccess: (res) => {
      const parsed = res.data;
      if (parsed.success) {
        setFormData({
          ...formData,
          transaction_type: parsed.transaction_type || 'customer_deposit',
          currency: parsed.currency || 'USD',
          amount: parsed.amount?.toString() || '',
          customer_name: parsed.sender || '',
          reference: parsed.reference || '',
          notes: `Parsed from ${parsed.provider || 'SMS'}: ${parsed.raw_text?.substring(0, 50)}...`,
        });
        setMode('manual');
        toast.success('SMS parsed successfully! Review and post.');
      } else {
        toast.error('Could not parse SMS. Try manual entry.');
      }
    },
    onError: () => toast.error('Failed to parse SMS'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Enter a valid amount'); return;
    }
    
    const data = {
      session_id: session.id,
      transaction_type: formData.transaction_type,
      currency: formData.currency,
      amount: parseFloat(formData.amount),
      customer_name: formData.customer_name || undefined,
      customer_phone: formData.customer_phone || undefined,
      reference: formData.reference || undefined,
      notes: formData.notes || undefined,
      provider: formData.provider || undefined,
      fee_amount: formData.fee_amount ? parseFloat(formData.fee_amount) : undefined,
    };
    
    if (formData.transaction_type.startsWith('fx_') && formData.fx_rate) {
      data.fx_rate = parseFloat(formData.fx_rate);
      data.fx_amount = parseFloat(formData.fx_amount);
    }
    
    transactionMutation.mutate(data);
  };

  if (!isOpen) return null;

  const isFx = formData.transaction_type.startsWith('fx_');
  const selectedType = TRANSACTION_TYPES.find(t => t.value === formData.transaction_type);
  const needsProvider = selectedType?.needsProvider;
  const showFee = ['card_purchase', 'mtn_cash_in', 'mtn_cash_out', 'orange_cash_in', 'orange_cash_out', 'bnb_cash_in', 'bnb_cash_out', 'xpress_cash_in', 'xpress_cash_out', 'remittance_send', 'remittance_payout'].includes(formData.transaction_type);

  // Group transaction types
  const groups = {};
  TRANSACTION_TYPES.forEach(t => {
    if (!groups[t.group]) groups[t.group] = [];
    groups[t.group].push(t);
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">New Transaction</h3>
            <div className="flex gap-2">
              <button onClick={() => setMode(mode === 'ai' ? 'manual' : 'ai')} className="flex items-center gap-1 text-sm text-primary hover:underline">
                <MessageSquare className="h-4 w-4" />
                {mode === 'ai' ? 'Manual Entry' : 'AI Parse SMS'}
              </button>
              <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
            </div>
          </div>

          {mode === 'ai' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Paste SMS / Screenshot Text</label>
                <textarea
                  value={smsText}
                  onChange={(e) => setSmsText(e.target.value)}
                  placeholder="Paste the SMS message here..."
                  rows={4}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm"
                />
              </div>
              <button
                onClick={() => parseMutation.mutate(smsText)}
                disabled={!smsText || parseMutation.isPending}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {parseMutation.isPending ? 'Parsing with AI...' : 'Parse SMS'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Transaction Type</label>
                <select
                  value={formData.transaction_type}
                  onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md"
                  data-testid="select-txn-type"
                >
                  {Object.entries(groups).map(([group, types]) => (
                    <optgroup key={group} label={group}>
                      {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </optgroup>
                  ))}
                </select>
                {selectedType && (
                  <p className={`text-xs mt-1 ${selectedType.direction === 'in' ? 'text-green-600' : selectedType.direction === 'out' ? 'text-red-600' : 'text-blue-600'}`}>
                    {selectedType.direction === 'in' ? 'INFLOW — Increases Till' : selectedType.direction === 'out' ? 'OUTFLOW — Decreases Till' : 'FX — Dual Currency'}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded-md">
                    <option value="USD">USD ($)</option>
                    <option value="LRD">LRD (L$)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" className="w-full px-3 py-2 bg-background border border-border rounded-md" data-testid="input-txn-amount" />
                </div>
              </div>
              {needsProvider && (
                <div>
                  <label className="block text-sm font-medium mb-1">Provider</label>
                  <select value={formData.provider} onChange={(e) => setFormData({ ...formData, provider: e.target.value })} className="w-full px-3 py-2 bg-background border border-border rounded-md">
                    <option value="">Select provider</option>
                    <option value="mtn">MTN</option>
                    <option value="orange">Orange</option>
                    <option value="bnb">BnB</option>
                    <option value="xpress">Xpress</option>
                  </select>
                </div>
              )}
              {isFx && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-muted/50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-1">Exchange Rate</label>
                    <input type="number" value={formData.fx_rate} onChange={(e) => setFormData({ ...formData, fx_rate: e.target.value })} placeholder="e.g., 150" className="w-full px-3 py-2 bg-background border border-border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{formData.currency === 'USD' ? 'LRD Amount' : 'USD Amount'}</label>
                    <input type="number" value={formData.fx_amount} onChange={(e) => setFormData({ ...formData, fx_amount: e.target.value })} placeholder="0.00" className="w-full px-3 py-2 bg-background border border-border rounded-md" />
                  </div>
                </div>
              )}
              {showFee && (
                <div>
                  <label className="block text-sm font-medium mb-1">Fee Amount (if applicable)</label>
                  <input type="number" value={formData.fee_amount} onChange={(e) => setFormData({ ...formData, fee_amount: e.target.value })} placeholder="0.00" className="w-full px-3 py-2 bg-background border border-border rounded-md" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Customer Name</label>
                  <input type="text" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} placeholder="Optional" className="w-full px-3 py-2 bg-background border border-border rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input type="text" value={formData.customer_phone} onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })} placeholder="Optional" className="w-full px-3 py-2 bg-background border border-border rounded-md" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reference</label>
                <input type="text" value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder="Transaction reference" className="w-full px-3 py-2 bg-background border border-border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} placeholder="Optional notes" className="w-full px-3 py-2 bg-background border border-border rounded-md" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted">Cancel</button>
                <button type="submit" disabled={transactionMutation.isPending} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50" data-testid="post-transaction-btn">
                  {transactionMutation.isPending ? 'Posting...' : 'Post Transaction'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// Stock Tab Component
function StockTab({ tellerId, session }) {
  const queryClient = useQueryClient();
  const [sellItem, setSellItem] = useState(null);
  const [sellQty, setSellQty] = useState(1);

  const { data: stock = [] } = useQuery({
    queryKey: ['teller-stock', tellerId],
    queryFn: () => stockSalesAPI.getTellerStock(tellerId).then(r => r.data),
    enabled: !!tellerId,
  });

  const sellMutation = useMutation({
    mutationFn: ({ itemId, qty, price }) => stockSalesAPI.create(session.id, itemId, qty, price, 'USD'),
    onSuccess: () => {
      queryClient.invalidateQueries(['teller-stock']);
      queryClient.invalidateQueries(['teller-transactions']);
      toast.success('Stock sold successfully!');
      setSellItem(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to sell stock'),
  });

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Allocated Stock</h3>
      {stock.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <ShoppingBag className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No stock allocated</p>
        </div>
      ) : (
        <div className="space-y-2">
          {stock.map(item => (
            <div key={item.id} className="p-4 border border-border rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium">{item.item_name}</p>
                <p className="text-sm text-muted-foreground">{item.item_category} • ${item.unit_price} each</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-lg font-bold">{item.remaining_quantity || item.quantity}</p>
                  <p className="text-xs text-muted-foreground">available</p>
                </div>
                <button
                  onClick={() => setSellItem(item)}
                  disabled={!session || session.status !== 'open'}
                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sell
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sell Modal */}
      {sellItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold mb-4">Sell {sellItem.item_name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Quantity</label>
                <input type="number" min="1" max={sellItem.remaining_quantity || sellItem.quantity} value={sellQty} onChange={(e) => setSellQty(parseInt(e.target.value) || 1)} className="w-full px-3 py-2 border border-border rounded-md" />
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Total: <span className="font-bold text-foreground">${(sellQty * sellItem.unit_price).toFixed(2)}</span></p>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setSellItem(null)} className="px-4 py-2 border border-border rounded-lg hover:bg-muted">Cancel</button>
                <button onClick={() => sellMutation.mutate({ itemId: sellItem.item_id, qty: sellQty, price: sellItem.unit_price })} disabled={sellMutation.isPending} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {sellMutation.isPending ? 'Selling...' : 'Confirm Sale'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Cash Request Component
function CashRequestSection({ tellerId, session }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ amount: '', currency: 'USD', reason: '' });

  const { data: requests = [] } = useQuery({
    queryKey: ['cash-requests', tellerId],
    queryFn: () => cashRequestAPI.getAll(tellerId).then(r => r.data),
    enabled: !!tellerId,
  });

  const createMutation = useMutation({
    mutationFn: (data) => cashRequestAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cash-requests']);
      toast.success('Cash request submitted!');
      setShowForm(false);
      setFormData({ amount: '', currency: 'USD', reason: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to submit request'),
  });

  const handleSubmit = () => {
    if (!formData.amount || !formData.reason) {
      toast.error('Please fill all fields'); return;
    }
    createMutation.mutate({ ...formData, session_id: session.id, amount: parseFloat(formData.amount) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Cash Requests</h3>
        {session?.status === 'open' && (
          <button onClick={() => setShowForm(!showForm)} className="px-3 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 text-sm">
            {showForm ? 'Cancel' : '+ Request Cash'}
          </button>
        )}
      </div>

      {showForm && (
        <div className="p-4 border border-border rounded-lg space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Amount</label>
              <input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" className="w-full px-3 py-2 border border-border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Currency</label>
              <select value={formData.currency} onChange={(e) => setFormData({ ...formData, currency: e.target.value })} className="w-full px-3 py-2 border border-border rounded-md">
                <option value="USD">USD</option>
                <option value="LRD">LRD</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reason</label>
            <input type="text" value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Why do you need more cash?" className="w-full px-3 py-2 border border-border rounded-md" />
          </div>
          <button onClick={handleSubmit} disabled={createMutation.isPending} className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg">
            {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      )}

      {requests.length > 0 ? (
        <div className="space-y-2">
          {requests.slice(0, 5).map(req => (
            <div key={req.id} className="p-3 border border-border rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium">{formatCurrency(req.amount, req.currency)}</p>
                <p className="text-sm text-muted-foreground">{req.reason}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${req.status === 'approved' ? 'bg-green-100 text-green-700' : req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                {req.status}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No cash requests</p>
      )}
    </div>
  );
}

// Main Teller Dashboard Component
export default function TellerDashboard() {
  const { user } = useAuth();
  const { selectedCompany, getDFSCompany } = useCompany();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showTxnModal, setShowTxnModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // For teller users, directly fetch their teller account (regardless of company context)
  const { data: myTellerAccount, isLoading: loadingMyAccount } = useQuery({
    queryKey: ['my-teller-account', user?.id],
    queryFn: async () => {
      const response = await tellerAccountAPI.getMyAccount();
      return response.data; // axios wraps response in .data
    },
    enabled: !!user?.id,
  });

  // Use the teller's own company for allocations etc
  const companyId = myTellerAccount?.company_id;
  const teller = myTellerAccount;

  // Get current session
  const { data: session } = useQuery({
    queryKey: ['teller-session', teller?.id],
    queryFn: () => sessionAPI.getCurrent(teller?.id).then(r => r.data).catch(() => null),
    enabled: !!teller?.id,
  });

  // Get allocations for this teller
  const { data: allocations = [] } = useQuery({
    queryKey: ['teller-allocations', teller?.id],
    queryFn: () => allocationAPI.getAll(companyId, 'pending').then(r => r.data.filter(a => a.teller_account_id === teller?.id)),
    enabled: !!teller?.id && !!companyId,
  });

  // Get transactions
  const { data: transactions = [] } = useQuery({
    queryKey: ['teller-transactions', session?.id],
    queryFn: () => tellerTransactionAPI.getAll(session?.id).then(r => r.data),
    enabled: !!session?.id,
  });

  const filteredTransactions = transactions.filter(t => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      t.reference?.toLowerCase().includes(term) ||
      t.customer_name?.toLowerCase().includes(term) ||
      t.customer_phone?.includes(term)
    );
  });

  const isSessionOpen = session?.status === 'open';

  // Show loading state
  if (loadingMyAccount) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading teller account...</p>
        </div>
      </div>
    );
  }

  // Show message if no teller account found
  if (!teller) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">No Teller Account Assigned</h2>
          <p className="text-muted-foreground">
            You don't have a teller account assigned to your user profile yet. 
            Please contact your supervisor or administrator to set up your teller station.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teller-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Teller Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">{teller?.name || 'Teller Station'} • {teller?.code}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isSessionOpen ? (
            <button
              onClick={() => setShowOpenModal(true)}
              disabled={allocations.length === 0 && !teller?.usd_current_balance && !teller?.lrd_current_balance}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              data-testid="open-session-btn"
            >
              <Power className="h-4 w-4" />
              Open Session
            </button>
          ) : (
            <button onClick={() => setShowCloseModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-testid="close-session-btn">
              <PowerOff className="h-4 w-4" />
              Close Session
            </button>
          )}
        </div>
      </div>

      {/* KPI Strip */}
      <KPIStrip session={{ ...session, total_transactions: transactions.length }} teller={teller} />

      {/* Session Not Active Warning */}
      {!isSessionOpen && (
        <div className="p-6 border-2 border-dashed border-orange-300 rounded-lg bg-orange-50 text-center">
          <PowerOff className="h-12 w-12 mx-auto mb-4 text-orange-500" />
          <h3 className="text-lg font-semibold text-orange-700">Session Not Active</h3>
          <p className="text-orange-600 mt-1">
            {allocations.length > 0
              ? `You have ${allocations.length} pending allocation(s). Click "Open Session" to confirm your cash and start the day.`
              : 'No allocations available. Contact your supervisor to receive cash allocation.'}
          </p>
        </div>
      )}

      {/* Tabs */}
      {isSessionOpen && (
        <>
          <div className="flex gap-2 border-b border-border">
            {['overview', 'transactions', 'stock', 'ai-capture'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              >
                {tab === 'ai-capture' ? 'AI Capture' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Quick Actions */}
              <div className="lg:col-span-2 space-y-4">
                <h3 className="font-semibold">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button onClick={() => setShowTxnModal(true)} className="p-4 border border-border rounded-lg hover:bg-muted/50 flex flex-col items-center gap-2" data-testid="new-transaction-btn">
                    <Plus className="h-6 w-6 text-primary" />
                    <span className="text-sm font-medium">New Transaction</span>
                  </button>
                  <button onClick={() => setActiveTab('stock')} className="p-4 border border-border rounded-lg hover:bg-muted/50 flex flex-col items-center gap-2">
                    <ShoppingBag className="h-6 w-6 text-green-600" />
                    <span className="text-sm font-medium">Sell Stock</span>
                  </button>
                  <button onClick={() => setActiveTab('ai-capture')} className="p-4 border border-border rounded-lg hover:bg-muted/50 flex flex-col items-center gap-2">
                    <MessageSquare className="h-6 w-6 text-blue-600" />
                    <span className="text-sm font-medium">AI Capture</span>
                  </button>
                  <button onClick={() => setShowCloseModal(true)} className="p-4 border border-border rounded-lg hover:bg-muted/50 flex flex-col items-center gap-2">
                    <PowerOff className="h-6 w-6 text-red-600" />
                    <span className="text-sm font-medium">Close Session</span>
                  </button>
                </div>

                {/* Recent Transactions */}
                <h3 className="font-semibold mt-6">Recent Transactions</h3>
                {transactions.length > 0 ? (
                  <div className="space-y-2">
                    {transactions.slice(0, 10).map(txn => {
                      const typeInfo = TRANSACTION_TYPES.find(t => t.value === txn.transaction_type) || {};
                      const Icon = typeInfo.icon || Receipt;
                      const isInflow = typeInfo.direction === 'in' || (txn.balance_after_usd > txn.balance_before_usd || txn.balance_after_lrd > txn.balance_before_lrd);
                      return (
                        <div key={txn.id} className="p-3 border border-border rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Icon className={`h-5 w-5 ${typeInfo.color || 'text-muted-foreground'}`} />
                            <div>
                              <p className="font-medium">{typeInfo.label || txn.transaction_type}</p>
                              <p className="text-xs text-muted-foreground">{txn.customer_name || 'N/A'} • {txn.reference}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${isInflow ? 'text-green-600' : 'text-red-600'}`}>
                              {isInflow ? '+' : '-'}{formatCurrency(txn.amount, txn.currency)}
                            </p>
                            <p className="text-xs text-muted-foreground">{new Date(txn.performed_at).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No transactions yet today</p>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <CashRequestSection tellerId={teller?.id} session={session} />
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search by reference, name, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background"
                  />
                </div>
                <button onClick={() => setShowTxnModal(true)} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">+ New</button>
              </div>
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left py-3 px-4">Time</th>
                      <th className="text-left py-3 px-4">Type</th>
                      <th className="text-left py-3 px-4">Customer</th>
                      <th className="text-left py-3 px-4">Reference</th>
                      <th className="text-right py-3 px-4">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map(txn => {
                      const typeInfo = TRANSACTION_TYPES.find(t => t.value === txn.transaction_type) || {};
                      const isInflow = typeInfo.direction === 'in' || (txn.balance_after_usd > txn.balance_before_usd || txn.balance_after_lrd > txn.balance_before_lrd);
                      return (
                      <tr key={txn.id} className="border-t border-border hover:bg-muted/30">
                        <td className="py-3 px-4">{new Date(txn.performed_at).toLocaleTimeString()}</td>
                        <td className="py-3 px-4">{typeInfo.label || txn.transaction_type}</td>
                        <td className="py-3 px-4">{txn.customer_name || '-'}</td>
                        <td className="py-3 px-4 font-mono text-xs">{txn.reference}</td>
                        <td className={`py-3 px-4 text-right font-bold ${isInflow ? 'text-green-600' : 'text-red-600'}`}>
                          {isInflow ? '+' : '-'}{formatCurrency(txn.amount, txn.currency)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredTransactions.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">No transactions found</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'stock' && <StockTab tellerId={teller?.id} session={session} />}

          {activeTab === 'ai-capture' && (
            <div className="max-w-xl">
              <h3 className="font-semibold mb-4">AI Capture</h3>
              <p className="text-muted-foreground mb-4">Paste an SMS message or text from a screenshot and let AI extract the transaction details.</p>
              <button onClick={() => { setShowTxnModal(true); }} className="w-full p-6 border-2 border-dashed border-primary/50 rounded-lg hover:bg-primary/5 flex flex-col items-center gap-3">
                <MessageSquare className="h-10 w-10 text-primary" />
                <span className="font-medium">Paste SMS / Upload Screenshot</span>
                <span className="text-sm text-muted-foreground">Click to open AI capture</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      <OpenSessionModal isOpen={showOpenModal} onClose={() => setShowOpenModal(false)} tellerId={teller?.id} allocations={allocations} companyId={companyId} />
      <CloseSessionModal isOpen={showCloseModal} onClose={() => setShowCloseModal(false)} session={session} teller={teller} />
      <NewTransactionModal isOpen={showTxnModal} onClose={() => setShowTxnModal(false)} session={session} />
    </div>
  );
}
